import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PhoneField from './PhoneField.jsx'

/* =========================================================================
   ContactModal — a context-aware enquiry dialog.

   CTA buttons across the site all pointed at /contact or #contact, which took
   the reader off whatever they were reading. This keeps them in place: the
   form opens over the page, and closing it returns them to the exact scroll
   position they were at.

   The dialog is not one form reused everywhere. Three signals are read at the
   moment of the click and composed into a panel that belongs to that click:

     1. the route      -> which service, so the visual identity, the proof
                          points and the pre-selected category all match the
                          page the reader is on
     2. the CTA label  -> what the reader asked for, so "Get the risk audit"
                          and "Talk to engineering" open different headlines,
                          different copy and a different submit button
     3. the section    -> the nearest heading above the button, echoed back so
                          the reader sees the dialog knew where they were

   Composing three small tables beats hand-writing a config per button: a CTA
   added to any page later inherits the right identity with no extra wiring,
   and a button that wants to override anything can say so with data-modal-*.

   Exports:
     ContactModalProvider — state, the dialog, and one capture-phase listener
     useContactModal()    — open({ ... }) from a component when needed
   ========================================================================= */

const ContactModalContext = createContext({ open: () => {}, close: () => {} })

export const useContactModal = () => useContext(ContactModalContext)

const INTERESTS = [
  'Software Development',
  'Website Development',
  'App Development',
  'UI/UX Design',
  'AI Automation',
  'Cyber Security',
  'Drone Development',
  'IoT Development',
  'Digital Marketing',
  'Social Media Management',
  'Business Consulting',
  'Other',
]

/* ---------------------------------------------------------------------------
   1. PAGE IDENTITY

   Every accent stays inside the brand's blue-to-indigo band, so each page
   feels distinct without any of them looking like a different company. The
   submit button and the field focus ring stay brand blue everywhere.

   noun   goes into headlines: "a fixed price for your custom software build"
   short  goes into shorter phrases: "your software audit"
   team   goes into "Talk to the ..." headlines
   proof  three checks the reader can verify, not slogans
--------------------------------------------------------------------------- */
const PAGES = {
  '/services/software-development': {
    category: 'Software Development',
    noun: 'custom software build',
    short: 'software',
    team: 'engineering team',
    icon: 'code',
    motif: 'code',
    from: '#1e3a8a',
    to: '#0b1e4d',
    proof: [
      'In production in 45 days, not 12 months',
      'Senior engineers writing the actual code',
      'Repository and infrastructure are yours',
    ],
  },
  '/services/website-development': {
    category: 'Website Development',
    noun: 'website build',
    short: 'website',
    team: 'web team',
    icon: 'browser',
    motif: 'browser',
    from: '#1d4ed8',
    to: '#0b2a6b',
    proof: [
      'Designed, built and live in 45 days',
      'Fast on a mid-range phone, not just your laptop',
      'You own the CMS, nothing is locked to us',
    ],
  },
  '/services/app-development': {
    category: 'App Development',
    noun: 'mobile app',
    short: 'app',
    team: 'app team',
    icon: 'phone',
    motif: 'devices',
    from: '#1e40af',
    to: '#0a1f52',
    proof: [
      'One codebase running iOS and Android',
      'Store submission and review handled for you',
      'Crash-free rate tracked from the first build',
    ],
  },
  '/services/ai-automation': {
    category: 'AI Automation',
    noun: 'AI build',
    short: 'workflow',
    team: 'AI team',
    icon: 'spark',
    motif: 'nodes',
    from: '#1e3a8a',
    to: '#111c44',
    proof: [
      'Wired into the tools your team already opens',
      'Human handoff on anything it should not decide',
      'Measured on hours saved, not on demo videos',
    ],
  },
  '/services/cyber-security': {
    category: 'Cyber Security',
    noun: 'security engagement',
    short: 'security',
    team: 'security team',
    icon: 'shield',
    motif: 'shield',
    from: '#0f254f',
    to: '#050d20',
    proof: [
      'Findings triaged with the engineers who fix them',
      'Evidence pack ready before the auditor asks',
      'A named responder on call, not a ticket queue',
    ],
  },
  '/services/iot-development': {
    category: 'IoT Development',
    noun: 'connected product',
    short: 'device fleet',
    team: 'hardware team',
    icon: 'chip',
    motif: 'signal',
    from: '#0e3a6b',
    to: '#06203f',
    proof: [
      'Board, firmware and cloud from one team',
      'Certified for the region you ship into',
      'Keeps working offline, syncs when it can',
    ],
  },
  '/services/drone-development': {
    category: 'Drone Development',
    noun: 'drone programme',
    short: 'fleet',
    team: 'flight team',
    icon: 'drone',
    motif: 'orbit',
    from: '#0c4a8f',
    to: '#062545',
    proof: [
      'Airframe, payload and ground station together',
      'Flight tested before it reaches you',
      'Regulatory paperwork handled alongside the build',
    ],
  },
  '/services/ui-ux-design': {
    category: 'UI/UX Design',
    noun: 'product design',
    short: 'interface',
    team: 'design team',
    icon: 'pen',
    motif: 'grid',
    from: '#2242c7',
    to: '#101a4a',
    proof: [
      'Research first, so the layout is not a guess',
      'A design system your developers can build from',
      'A prototype you can click before anyone codes',
    ],
  },
  '/services/digital-marketing': {
    category: 'Digital Marketing',
    noun: 'growth engine',
    short: 'marketing',
    team: 'growth team',
    icon: 'chart',
    motif: 'bars',
    from: '#1747d6',
    to: '#0a1e57',
    proof: [
      'Every rupee traced to the customer it brought',
      'Tested and rebuilt on a 14 day rhythm',
      'One dashboard the whole team can read',
    ],
  },
  '/services/social-media-management': {
    category: 'Social Media Management',
    noun: 'social presence',
    short: 'social',
    team: 'social team',
    icon: 'share',
    motif: 'pulse',
    from: '#1b4bd1',
    to: '#0d2050',
    proof: [
      'Monthly shoot, daily posting, nothing skipped',
      'Comments and DMs answered, not left sitting',
      'Reported on reach that actually converts',
    ],
  },
  '/services/business-consulting': {
    category: 'Business Consulting',
    noun: 'growth plan',
    short: 'business',
    team: 'partner team',
    icon: 'compass',
    motif: 'path',
    from: '#2c3fbf',
    to: '#121a4f',
    proof: [
      'Partners who have built and sold companies',
      'Pre-seed through Series C, whichever you are at',
      '200+ founders supported across IN, MENA and SEA',
    ],
  },
  '/careers': {
    category: 'Other',
    noun: 'application',
    short: 'role',
    team: 'hiring team',
    icon: 'users',
    motif: 'spectrum',
    from: '#1d4ed8',
    to: '#0b1e4d',
    proof: [
      'Every application read by a person on the team',
      'Two rounds, both with people you would work with',
      'A clear yes or no, never silence',
    ],
  },
  '/portfolio': {
    category: 'Other',
    noun: 'project',
    short: 'project',
    team: 'delivery team',
    icon: 'layers',
    motif: 'spectrum',
    from: '#1740b8',
    to: '#0a1a45',
    proof: [
      'Ask for the detail behind any project on this page',
      'We can put you in touch with the client',
      'The same team is available for yours',
    ],
  },
}

const DEFAULT_PAGE = {
  category: '',
  noun: 'project',
  short: 'business',
  team: 'team',
  icon: 'spark',
  motif: 'spectrum',
  from: '#1d4ed8',
  to: '#0b1e4d',
  proof: [
    'Up to 90% lower cost than a traditional agency build',
    'Websites, apps, AI, IoT and security under one roof',
    '40+ clients, 4.9 out of 5 average rating',
  ],
}

/* Aliases keep one identity across routes describing the same work. */
const PAGE_ALIASES = {
  '/services/web-development': '/services/website-development',
}

function pageProfile(pathname) {
  const path = (PAGE_ALIASES[pathname] || pathname || '/').replace(/\/+$/, '') || '/'
  if (PAGES[path]) return PAGES[path]
  const prefix = Object.keys(PAGES).find((k) => path.startsWith(k + '/'))
  return prefix ? PAGES[prefix] : DEFAULT_PAGE
}

/* ---------------------------------------------------------------------------
   2. CTA INTENT

   What the reader asked for, read off the button they pressed. Ordered: the
   first match wins, so the specific tests sit above the general ones.
--------------------------------------------------------------------------- */
const INTENTS = [
  {
    id: 'audit',
    test: /audit|assess|review my|risk|health ?check/i,
    eyebrow: 'Free, no obligation',
    title: (p) => `Your free ${p.short} audit`,
    desc: (p) =>
      `Tell us what is running today. A senior specialist reviews it and comes back with what we would fix first, in what order, and what it would cost. The review itself is free, and you keep it either way.`,
    submit: 'Request my free audit',
    seed: (p) => `I would like the free ${p.short} audit.`,
  },
  {
    id: 'quote',
    test: /quote|pricing|price|cost|estimate|budget/i,
    eyebrow: 'Fixed price, no surprises',
    title: (p) => `A fixed price for your ${p.noun}`,
    desc: () =>
      'Send us the outline and you get a fixed quote, a delivery date and the names of the people who would build it. No hourly billing, and no scope creep halfway through.',
    submit: 'Send my quote request',
    seed: (p) => `I need a quote for a ${p.noun}.`,
  },
  {
    id: 'scoping',
    test: /scop|spec|requirement|blueprint|plan (a|my|the)/i,
    eyebrow: '30 minutes, free',
    title: () => 'Book a scoping call',
    desc: (p) =>
      `Half an hour with a senior engineer, not a sales rep. We map the ${p.noun}, name the risks early, and tell you what it costs before you commit to anything.`,
    submit: 'Book my scoping call',
    seed: (p) => `I would like to book a scoping call about a ${p.noun}.`,
  },
  {
    id: 'discovery',
    test: /discovery|discover|map your|map my|workshop/i,
    eyebrow: 'Discovery session',
    title: () => 'Map it before you build it',
    desc: (p) =>
      `We walk your current setup, find the parts of your ${p.short} worth changing first, and show you what is realistic in 45 days. You leave with the map whether or not you hire us.`,
    submit: 'Book my discovery session',
    seed: () => 'I would like to book a discovery session.',
  },
  {
    id: 'strategy',
    test: /strateg|partner|advis|consult|mentor/i,
    eyebrow: 'Senior led, always',
    title: () => 'Book a strategy call',
    desc: () =>
      'You get a partner who has built and sold companies, not an account manager. Bring the decision you are stuck on and we will work through it with you.',
    submit: 'Book my strategy call',
    seed: () => 'I would like to book a strategy call.',
  },
  {
    id: 'demo',
    test: /demo|see it|walkthrough|show me|try it/i,
    eyebrow: 'Live walkthrough',
    title: (p) => `See the ${p.short} working`,
    desc: () =>
      'A live walkthrough on your own data, not a recorded slideshow. Bring your team and ask the awkward questions while we are still on the call.',
    submit: 'Book my walkthrough',
    seed: () => 'I would like a live demo.',
  },
  {
    id: 'hiring',
    test: /hiring|career|join|apply|recruit/i,
    eyebrow: 'Careers at Spectranox',
    title: () => 'Talk to a hiring lead',
    desc: () =>
      'Tell us what you are good at and what you want to work on next. Every message is read by someone on the team you would be joining, and you get a clear answer either way.',
    submit: 'Send my message',
    seed: () => 'I am interested in a role at Spectranox.',
  },
  {
    id: 'support',
    test: /support|help|issue|problem|troubleshoot/i,
    eyebrow: 'Existing client',
    title: () => 'Get support from the team',
    desc: () =>
      'Tell us what is happening and we will put the right engineer on it. If something you already run with us is broken, say so and it moves to the front.',
    submit: 'Send to support',
    seed: () => 'I need support with something already live.',
  },
  {
    id: 'start',
    test: /start|get started|begin|kick ?off|launch|build my|request a/i,
    eyebrow: 'Start here',
    title: (p) => `Start your ${p.noun}`,
    desc: (p) =>
      `Tell us where you are today and we come back with a scope, a timeline and a price for the ${p.noun}. Usually within one working day.`,
    submit: 'Start my project',
    seed: (p) => `I would like to start a ${p.noun}.`,
  },
  {
    id: 'talk',
    test: /talk|speak|call|contact|reach|discuss|connect|book/i,
    eyebrow: 'Straight to the people doing the work',
    title: (p) => `Talk to the ${p.team}`,
    desc: () =>
      'No call centre and no gatekeeping. Your message reaches the people who would actually do the work, and one of them replies within a working day.',
    submit: 'Send my message',
    seed: () => '',
  },
]

const DEFAULT_INTENT = {
  id: 'default',
  eyebrow: 'Tell us the shape of it',
  title: () => "Tell us what you're building",
  desc: () =>
    'A few lines is enough to start. You get a scope, a timeline and a price back, usually within one working day, from the person who would run the build.',
  submit: 'Send enquiry',
  seed: () => '',
}

function intentProfile(label, path) {
  if (/^\/careers/.test(path || '')) return INTENTS.find((i) => i.id === 'hiring')
  const text = (label || '').trim()
  if (!text) return DEFAULT_INTENT
  return INTENTS.find((i) => i.test.test(text)) || DEFAULT_INTENT
}

/* ---------------------------------------------------------------------------
   3. SECTION

   The heading directly above the button, echoed back to the reader so the
   dialog visibly knows where they were. data-modal-section on any wrapper
   overrides it where the nearest heading is not the useful one.
--------------------------------------------------------------------------- */
/* Several pages ship a desktop hero and a mobile hero and hide one of them.
   A hidden element's innerText quietly falls back to textContent, which has no
   line-break awareness, so "Drones for every / mission" comes back as
   "everymission". Only ever read a heading that is actually rendered. */
function visibleHeading(holder) {
  const heads = holder.querySelectorAll?.('h1, h2, h3')
  if (!heads) return null
  for (const h of heads) {
    if (h.offsetParent === null) continue
    const r = h.getBoundingClientRect()
    if (r.width > 2 && r.height > 2) return h
  }
  return null
}

function sectionLabel(el, ctaLabel) {
  const holder = el.closest?.('[data-modal-section], section')
  if (!holder) return ''
  const explicit = holder.getAttribute?.('data-modal-section')
  if (explicit) return explicit
  const head = visibleHeading(holder)
  if (!head) return ''
  const text = (head.innerText || head.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.:]+$/, '')
  if (!text || text.length > 64) return ''
  if (ctaLabel && text.toLowerCase() === ctaLabel.toLowerCase()) return ''
  return text
}

/* Which links should open the dialog rather than navigate.
   Only button-shaped anchors qualify: nav items and footer links are plain
   text and should still take the reader to the contact page, because that is
   what a visitor clicking a nav entry is asking for. */
function isCtaLink(a) {
  const href = a.getAttribute('href') || ''
  const target = a.getAttribute('target')
  if (target && target !== '_self') return false
  if (a.dataset.noModal !== undefined) return false

  const pointsAtContact =
    href === '/contact' ||
    href.startsWith('/contact?') ||
    href.startsWith('/contact#') ||
    href === '#contact' ||
    href.endsWith('#contact')
  if (!pointsAtContact) return false

  /* Button-shaped: a rounded corner AND a resting fill or outline. The rounded
     corner alone is not enough -- the mobile nav rows use rounded-xl purely for
     their hover surface, and those are navigation, not CTAs. */
  const cls = (a.className || '').toString()
  if (!/\brounded-(full|lg|xl|2xl|md)\b/.test(cls)) return false
  const hasFill = /(^|\s)bg-(?!transparent)/.test(cls) || /(^|\s)(border|border-2|ring-1|ring-2)(\s|$)/.test(cls)
  return hasFill || /\bbtn(-|\s|$)/.test(cls)
}

/* Pull a sensible default subject out of the link the reader clicked, so a
   "Book a scoping call" button lands on the form with that already chosen. */
function subjectFromHref(href) {
  try {
    const q = href.split('?')[1]
    if (!q) return ''
    const v = new URLSearchParams(q).get('subject')
    return v ? decodeURIComponent(v) : ''
  } catch {
    return ''
  }
}

/* Compose the three tables into the panel the reader is about to see. */
function composeContext(raw) {
  const page = pageProfile(raw.path)
  const intent = intentProfile(raw.overrideIntent || raw.label, raw.path)
  return {
    ...page,
    eyebrow: raw.eyebrow || intent.eyebrow,
    title: raw.title || intent.title(page),
    desc: raw.desc || intent.desc(page),
    submit: raw.submit || intent.submit,
    seed: raw.subject ? `Enquiry: ${raw.subject}` : intent.seed(page),
    section: raw.section || '',
    label: raw.label || '',
    path: raw.path || '/',
    intentId: intent.id,
  }
}

/* ---------------------------------------------------------------------------
   Motifs — the animated backdrop behind the left panel. One per page family,
   so the dialog opening on Cyber Security does not look like the one opening
   on Digital Marketing. Transform and opacity only, so they stay cheap.
--------------------------------------------------------------------------- */
function Motif({ kind }) {
  const cls = 'pointer-events-none absolute inset-0 h-full w-full'
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' }

  if (kind === 'code') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <g className="cm-drift" opacity=".5">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <rect key={i} x="40" y={38 + i * 20} width={i % 2 ? 70 : 100} height="5" rx="2.5" fill="currentColor" opacity={0.25 + (i % 3) * 0.12} />
          ))}
        </g>
        <path d="M28 60 L14 100 L28 140" {...s} opacity=".55" className="cm-pulse" />
        <path d="M172 60 L186 100 L172 140" {...s} opacity=".55" className="cm-pulse cm-d2" />
        <rect x="30" y="52" width="140" height="4" rx="2" fill="currentColor" opacity=".8" className="cm-scan" />
      </svg>
    )
  }

  if (kind === 'browser') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <g className="cm-drift">
          <rect x="26" y="42" width="148" height="112" rx="10" {...s} opacity=".5" />
          <path d="M26 62 H174" {...s} opacity=".4" />
          {[38, 48, 58].map((cx) => (
            <circle key={cx} cx={cx} cy="52" r="3" fill="currentColor" opacity=".55" />
          ))}
          {[76, 92, 108, 124].map((y, i) => (
            <rect key={y} x="40" y={y} width={i % 2 ? 84 : 118} height="6" rx="3" fill="currentColor" opacity={0.18 + i * 0.06} />
          ))}
        </g>
        <rect x="40" y="140" width="40" height="6" rx="3" fill="currentColor" opacity=".9" className="cm-grow" />
      </svg>
    )
  }

  if (kind === 'devices') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <g className="cm-drift">
          <rect x="30" y="52" width="62" height="108" rx="12" {...s} opacity=".55" />
          <rect x="104" y="66" width="70" height="82" rx="9" {...s} opacity=".4" />
          <path d="M52 62 H70" {...s} opacity=".6" />
          {[80, 96, 112, 128].map((y, i) => (
            <rect key={y} x="42" y={y} width={i % 2 ? 26 : 38} height="5" rx="2.5" fill="currentColor" opacity=".28" />
          ))}
        </g>
        <circle cx="139" cy="107" r="15" {...s} opacity=".7" className="cm-ripple" />
      </svg>
    )
  }

  if (kind === 'nodes') {
    const pts = [[52, 62], [140, 54], [100, 104], [46, 148], [154, 140]]
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <g opacity=".45">
          {pts.slice(1).map(([x, y], i) => (
            <line key={i} x1={pts[0][0]} y1={pts[0][1]} x2={x} y2={y} stroke="currentColor" strokeWidth="1.2" />
          ))}
          <line x1={pts[2][0]} y1={pts[2][1]} x2={pts[4][0]} y2={pts[4][1]} stroke="currentColor" strokeWidth="1.2" />
          <line x1={pts[2][0]} y1={pts[2][1]} x2={pts[3][0]} y2={pts[3][1]} stroke="currentColor" strokeWidth="1.2" />
        </g>
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === 2 ? 9 : 5.5} fill="currentColor" opacity=".85" className={`cm-node cm-d${i % 4}`} />
        ))}
      </svg>
    )
  }

  if (kind === 'shield') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <path d="M100 34 L158 58 V104 C158 138 132 158 100 168 C68 158 42 138 42 104 V58 Z" {...s} opacity=".5" className="cm-drift" />
        <path d="M100 46 L146 65 V104 C146 130 126 147 100 156" {...s} opacity=".28" />
        <g className="cm-sweep" style={{ transformOrigin: '100px 100px' }}>
          <path d="M100 100 L100 44" stroke="currentColor" strokeWidth="2" opacity=".85" />
        </g>
        <circle cx="100" cy="100" r="4" fill="currentColor" opacity=".9" />
      </svg>
    )
  }

  if (kind === 'signal') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <rect x="82" y="82" width="36" height="36" rx="7" {...s} opacity=".8" />
        <path d="M118 88 h14 M118 100 h14 M118 112 h14 M82 88 h-14 M82 100 h-14 M82 112 h-14" {...s} opacity=".6" />
        {[26, 44, 62].map((r, i) => (
          <circle key={r} cx="100" cy="100" r={r} {...s} opacity=".5" className={`cm-ripple cm-d${i}`} />
        ))}
      </svg>
    )
  }

  if (kind === 'orbit') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <ellipse cx="100" cy="100" rx="66" ry="26" {...s} opacity=".45" className="cm-spin" style={{ transformOrigin: '100px 100px' }} />
        <ellipse cx="100" cy="100" rx="26" ry="66" {...s} opacity=".3" />
        <g className="cm-drift">
          <path d="M74 92 h52 M100 92 v18" {...s} opacity=".8" />
          <circle cx="74" cy="92" r="9" {...s} opacity=".7" />
          <circle cx="126" cy="92" r="9" {...s} opacity=".7" />
          <circle cx="100" cy="114" r="6" fill="currentColor" opacity=".85" />
        </g>
      </svg>
    )
  }

  if (kind === 'grid') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <g opacity=".3">
          {[40, 70, 100, 130, 160].map((v) => (
            <g key={v}>
              <line x1={v} y1="30" x2={v} y2="170" stroke="currentColor" strokeWidth="1" />
              <line x1="30" y1={v} x2="170" y2={v} stroke="currentColor" strokeWidth="1" />
            </g>
          ))}
        </g>
        <rect x="70" y="70" width="60" height="42" rx="8" {...s} opacity=".85" className="cm-drift" />
        <path d="M118 118 l6 22 5-9 9 3 z" fill="currentColor" opacity=".9" className="cm-cursor" />
      </svg>
    )
  }

  if (kind === 'bars') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <line x1="34" y1="152" x2="170" y2="152" {...s} opacity=".45" />
        {[36, 58, 84, 104, 126].map((h, i) => (
          <rect
            key={i}
            x={46 + i * 24}
            y={152 - h}
            width="14"
            height={h}
            rx="4"
            fill="currentColor"
            opacity={0.3 + i * 0.12}
            className={`cm-rise cm-d${i % 4}`}
            style={{ transformOrigin: `${53 + i * 24}px 152px` }}
          />
        ))}
        <path d="M46 116 L70 100 L94 78 L118 62 L146 40" {...s} strokeWidth="2" opacity=".8" className="cm-draw" />
      </svg>
    )
  }

  if (kind === 'pulse') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <path d="M24 104 h34 l10 -30 l14 62 l12 -44 l10 22 h24 l10 -16 l12 30 h34" {...s} strokeWidth="2" opacity=".75" className="cm-draw" />
        {[[54, 148], [100, 158], [146, 148]].map(([x, y], i) => (
          <circle key={x} cx={x} cy={y} r="7" fill="currentColor" opacity=".35" className={`cm-node cm-d${i}`} />
        ))}
      </svg>
    )
  }

  if (kind === 'path') {
    return (
      <svg viewBox="0 0 200 200" className={cls} aria-hidden>
        <path d="M34 156 C70 156 62 108 100 108 C138 108 130 52 168 52" {...s} strokeWidth="2" strokeDasharray="5 7" opacity=".55" className="cm-flow" />
        {[[34, 156], [100, 108], [168, 52]].map(([x, y], i) => (
          <g key={x}>
            <circle cx={x} cy={y} r="13" {...s} opacity=".5" className={`cm-ripple cm-d${i}`} />
            <circle cx={x} cy={y} r="5.5" fill="currentColor" opacity=".9" />
          </g>
        ))}
      </svg>
    )
  }

  /* spectrum — the house default */
  return (
    <svg viewBox="0 0 200 200" className={cls} aria-hidden>
      {[30, 48, 66, 84].map((r, i) => (
        <circle key={r} cx="100" cy="112" r={r} {...s} opacity={0.5 - i * 0.09} className={`cm-ripple cm-d${i}`} />
      ))}
      <path d="M40 112 A60 60 0 0 1 160 112" {...s} strokeWidth="2.4" opacity=".8" className="cm-draw" />
      <circle cx="100" cy="112" r="7" fill="currentColor" opacity=".95" />
    </svg>
  )
}

/* Small glyph set for the badge at the top of the left panel. */
function Glyph({ name, className }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' }
  const paths = {
    code: <path d="M8 6l-5 6 5 6M16 6l5 6-5 6" {...p} />,
    browser: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2.5" {...p} />
        <path d="M3 9h18M7 6.5h.01" {...p} />
      </>
    ),
    phone: (
      <>
        <rect x="6" y="2.5" width="12" height="19" rx="2.5" {...p} />
        <path d="M10.5 18.5h3" {...p} />
      </>
    ),
    spark: <path d="M12 2.5l2.2 5.9 5.9 2.2-5.9 2.2L12 18.7l-2.2-5.9L3.9 10.6l5.9-2.2z" {...p} />,
    shield: <path d="M12 2.8l7.5 3v5.4c0 4.6-3.2 7.9-7.5 9.4-4.3-1.5-7.5-4.8-7.5-9.4V5.8z" {...p} />,
    chip: (
      <>
        <rect x="7" y="7" width="10" height="10" rx="2" {...p} />
        <path d="M10 3v4M14 3v4M10 17v4M14 17v4M3 10h4M3 14h4M17 10h4M17 14h4" {...p} />
      </>
    ),
    drone: (
      <>
        <path d="M7 8h10M12 8v5" {...p} />
        <circle cx="7" cy="8" r="3.2" {...p} />
        <circle cx="17" cy="8" r="3.2" {...p} />
        <rect x="9.5" y="13" width="5" height="5" rx="1.4" {...p} />
      </>
    ),
    pen: <path d="M4 20l4.5-1 10-10a2.5 2.5 0 10-3.5-3.5l-10 10z" {...p} />,
    chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" {...p} />,
    share: (
      <>
        <circle cx="18" cy="5.5" r="2.8" {...p} />
        <circle cx="6" cy="12" r="2.8" {...p} />
        <circle cx="18" cy="18.5" r="2.8" {...p} />
        <path d="M8.4 10.7l7.2-3.9M8.4 13.3l7.2 3.9" {...p} />
      </>
    ),
    compass: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M15.5 8.5l-2 5-5 2 2-5z" {...p} />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3.4" {...p} />
        <path d="M2.8 20a6.2 6.2 0 0112.4 0M16.5 5.2a3.4 3.4 0 010 5.6M18 20a5.6 5.6 0 00-2-4.3" {...p} />
      </>
    ),
    layers: <path d="M12 3l9 5-9 5-9-5zM3 13l9 5 9-5M3 17l9 4 9-4" {...p} />,
  }
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      {paths[name] || paths.spark}
    </svg>
  )
}

function Dialog({ onClose, ctx }) {
  const panelRef = useRef(null)
  const firstFieldRef = useRef(null)
  const restoreFocusRef = useRef(null)

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: ctx.category || '',
    message: ctx.seed || '',
  })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [error, setError] = useState('')

  const API_BASE = import.meta.env.VITE_API_BASE || '/api'
  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  /* Esc to close, and keep Tab inside the dialog while it is open. */
  useEffect(() => {
    restoreFocusRef.current = document.activeElement
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)

    /* Lock the page behind the dialog without losing scroll position: padding
       compensates for the scrollbar so the layout does not jump. */
    const { body } = document
    const prevOverflow = body.style.overflow
    const prevPad = body.style.paddingRight
    const gap = window.innerWidth - document.documentElement.clientWidth
    body.style.overflow = 'hidden'
    if (gap > 0) body.style.paddingRight = `${gap}px`

    const t = setTimeout(() => firstFieldRef.current?.focus(), 60)

    return () => {
      clearTimeout(t)
      document.removeEventListener('keydown', onKey, true)
      body.style.overflow = prevOverflow
      body.style.paddingRight = prevPad
      restoreFocusRef.current?.focus?.()
    }
  }, [onClose])

  const onSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setError('')

    /* The context the reader came from travels with the enquiry, so whoever
       picks it up knows which page and which button produced it without
       having to ask. message is the only free-text field the API accepts. */
    const trail = [
      ctx.label && `Button: ${ctx.label}`,
      ctx.section && `Section: ${ctx.section}`,
      ctx.path && `Page: ${ctx.path}`,
    ].filter(Boolean)
    const body = [form.message.trim(), trail.length ? `--\n${trail.join('\n')}` : '']
      .filter(Boolean)
      .join('\n\n')

    try {
      const res = await fetch(`${API_BASE}/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          interest: form.interest,
          message: body,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success) {
        setStatus('success')
      } else {
        setStatus('error')
        setError(data.message || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setError('Could not reach the server. Please email us directly.')
    }
  }

  const field =
    'block w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[16px] text-slate-900 ' +
    'placeholder:text-slate-400 outline-none transition-all focus:border-[#1d4ed8] focus:ring-2 ' +
    'focus:ring-[#1d4ed8]/25 sm:py-3 sm:text-[14px]'

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <style>{`
        @keyframes cm-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cm-rise {
          from { opacity: 0; transform: translateY(24px) scale(.98) }
          to   { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes cm-in {
          from { opacity: 0; transform: translateY(10px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        @keyframes cm-drift  { 0%,100% { transform: translateY(-4px) } 50% { transform: translateY(4px) } }
        @keyframes cm-ripple { 0% { transform: scale(.72); opacity: .6 } 100% { transform: scale(1.25); opacity: 0 } }
        @keyframes cm-node   { 0%,100% { transform: scale(1); opacity: .55 } 50% { transform: scale(1.35); opacity: 1 } }
        @keyframes cm-sweep  { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes cm-spin   { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes cm-scan   { 0% { transform: translateY(0); opacity: 0 } 12%,88% { opacity: .9 } 100% { transform: translateY(96px); opacity: 0 } }
        @keyframes cm-grow   { 0%,100% { transform: scaleX(.4) } 50% { transform: scaleX(1) } }
        @keyframes cm-riseup { 0% { transform: scaleY(.35) } 55%,100% { transform: scaleY(1) } }
        @keyframes cm-draw   { 0% { stroke-dasharray: 0 400 } 60%,100% { stroke-dasharray: 400 0 } }
        @keyframes cm-flow   { to { stroke-dashoffset: -48 } }
        @keyframes cm-cursor { 0%,100% { transform: translate(0,0) } 50% { transform: translate(-14px,-16px) } }

        .cm-drift  { animation: cm-drift 7s ease-in-out infinite }
        .cm-ripple { animation: cm-ripple 3.2s ease-out infinite; transform-origin: center }
        .cm-node   { animation: cm-node 2.6s ease-in-out infinite; transform-origin: center }
        .cm-pulse  { animation: cm-node 3s ease-in-out infinite }
        .cm-sweep  { animation: cm-sweep 4.5s linear infinite }
        .cm-spin   { animation: cm-spin 16s linear infinite }
        .cm-scan   { animation: cm-scan 3.6s ease-in-out infinite }
        .cm-grow   { animation: cm-grow 3s ease-in-out infinite; transform-origin: left center }
        .cm-rise   { animation: cm-riseup 3.4s ease-in-out infinite }
        .cm-draw   { animation: cm-draw 4s ease-in-out infinite }
        .cm-flow   { animation: cm-flow 2.4s linear infinite }
        .cm-cursor { animation: cm-cursor 5s ease-in-out infinite }
        .cm-d0 { animation-delay: 0s }   .cm-d1 { animation-delay: .5s }
        .cm-d2 { animation-delay: 1s }   .cm-d3 { animation-delay: 1.5s }

        @media (prefers-reduced-motion: reduce) {
          .cm-backdrop, .cm-panel, .cm-anim,
          .cm-drift, .cm-ripple, .cm-node, .cm-pulse, .cm-sweep, .cm-spin,
          .cm-scan, .cm-grow, .cm-rise, .cm-draw, .cm-flow, .cm-cursor {
            animation: none !important
          }
        }
      `}</style>

      <div
        className="cm-backdrop absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        style={{ animation: 'cm-fade .18s ease-out both' }}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className="cm-panel relative max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-h-[90vh] sm:max-w-3xl sm:rounded-3xl"
        style={{ animation: 'cm-rise .3s cubic-bezier(.22,1,.36,1) both' }}
      >
        {/* One close button serves both layouts: on mobile it sits over the
            gradient panel, on sm+ the top right corner is the white form. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3.5 top-3.5 z-20 grid size-9 place-items-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25 sm:bg-transparent sm:text-slate-400 sm:backdrop-blur-0 sm:hover:bg-slate-100 sm:hover:text-slate-900"
        >
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {status === 'success' ? (
          <div className="px-6 py-14 text-center sm:px-10">
            <span
              className="mx-auto grid size-14 place-items-center rounded-full text-white"
              style={{ backgroundImage: `linear-gradient(140deg, ${ctx.from}, ${ctx.to})` }}
            >
              <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <h2 id="contact-modal-title" className="mt-5 font-display text-2xl font-bold text-slate-900">
              Thanks, we have it
            </h2>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
              {ctx.intentId === 'hiring'
                ? 'A hiring lead reads every message. You will hear back either way, usually within two working days.'
                : 'We reply within one working day with a scope, a timeline and a price. No call centre in between.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-7 inline-flex items-center justify-center rounded-full bg-[#1d4ed8] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1e40af]"
            >
              Back to reading
            </button>
          </div>
        ) : (
          <div className="sm:grid sm:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)]">

            {/* ------------------ context panel ------------------ */}
            {/* Desktop only. On a phone this panel pushed the form most of a
                screen down, so the thing the reader came to do was below the
                fold behind a wall of marketing. */}
            <aside
              className="relative hidden overflow-hidden text-white sm:flex sm:flex-col sm:px-7 sm:py-8"
              style={{ backgroundImage: `linear-gradient(158deg, ${ctx.from} 0%, ${ctx.to} 100%)` }}
            >
              <div aria-hidden className="pointer-events-none absolute inset-0 text-white/25">
                <Motif kind={ctx.motif} />
              </div>
              <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 size-52 rounded-full bg-white/10 blur-3xl" />

              <div className="relative flex flex-1 flex-col">
                <span
                  className="cm-anim grid size-11 place-items-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 backdrop-blur sm:size-12"
                  style={{ animation: 'cm-in .4s ease-out .05s both' }}
                >
                  <Glyph name={ctx.icon} className="size-5 sm:size-6" />
                </span>

                <p
                  className="cm-anim mt-5 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70"
                  style={{ animation: 'cm-in .4s ease-out .1s both' }}
                >
                  {ctx.eyebrow}
                </p>

                <h2
                  id="contact-modal-title"
                  className="cm-anim mt-2 pr-10 font-display text-[24px] font-bold leading-[1.15] tracking-tight sm:pr-0 sm:text-[27px]"
                  style={{ animation: 'cm-in .45s ease-out .15s both' }}
                >
                  {ctx.title}
                </h2>

                <p
                  className="cm-anim mt-3 text-[13px] leading-relaxed text-white/75 sm:text-sm"
                  style={{ animation: 'cm-in .45s ease-out .2s both' }}
                >
                  {ctx.desc}
                </p>

                {/* The proof list is what earns the form fields. It is the
                    first thing to go on a short mobile screen. */}
                <ul className="mt-6 hidden space-y-2.5 sm:block">
                  {ctx.proof.map((line, i) => (
                    <li
                      key={line}
                      className="cm-anim flex items-start gap-2.5 text-[13px] leading-snug text-white/85"
                      style={{ animation: `cm-in .45s ease-out ${0.26 + i * 0.07}s both` }}
                    >
                      <svg viewBox="0 0 24 24" className="mt-0.5 size-4 shrink-0 text-white/70" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      {line}
                    </li>
                  ))}
                </ul>

                {ctx.section && (
                  <p
                    className="cm-anim mt-6 flex max-w-full items-center gap-1.5 self-start rounded-full bg-white/[0.12] px-3 py-1.5 text-[11px] font-medium text-white/80 ring-1 ring-white/15"
                    style={{ animation: 'cm-in .45s ease-out .5s both' }}
                  >
                    <svg viewBox="0 0 24 24" className="size-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M4 6h16M4 12h10M4 18h7" />
                    </svg>
                    <span className="truncate">You were reading: {ctx.section}</span>
                  </p>
                )}

                <div
                  className="cm-anim mt-6 flex items-center gap-2 border-t border-white/15 pt-4 text-[12px] text-white/65 sm:mt-auto"
                  style={{ animation: 'cm-in .45s ease-out .55s both' }}
                >
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex size-2 animate-ping rounded-full bg-emerald-300 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-emerald-300" />
                  </span>
                  Replies within one working day
                </div>
              </div>
            </aside>

            {/* ------------------ form ------------------ */}
            <div className="sm:contents">
              {/* Mobile header. Carries the same eyebrow and title the aside
                  shows on desktop, in the height of a heading rather than a
                  full panel. */}
              <div
                className="relative overflow-hidden px-6 pb-5 pt-7 text-white sm:hidden"
                style={{ backgroundImage: `linear-gradient(158deg, ${ctx.from} 0%, ${ctx.to} 100%)` }}
              >
                <div aria-hidden className="pointer-events-none absolute -right-10 -top-12 size-40 rounded-full bg-white/10 blur-3xl" />
                <div className="relative">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/70">{ctx.eyebrow}</p>
                  <h2 id="contact-modal-title-m" className="mt-1.5 pr-10 font-display text-[20px] font-bold leading-[1.2] tracking-tight">
                    {ctx.title}
                  </h2>
                  <p className="mt-2.5 flex items-center gap-2 text-[11.5px] text-white/70">
                    <span className="relative flex size-1.5">
                      <span className="absolute inline-flex size-1.5 animate-ping rounded-full bg-emerald-300 opacity-75" />
                      <span className="relative inline-flex size-1.5 rounded-full bg-emerald-300" />
                    </span>
                    Replies within one working day
                  </p>
                </div>
              </div>

            <form onSubmit={onSubmit} className="px-6 py-6 sm:px-8 sm:py-8">
              <div className="space-y-3">
                <input
                  ref={firstFieldRef}
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={update}
                  placeholder="Full name"
                  autoComplete="name"
                  autoCapitalize="words"
                  enterKeyHint="next"
                  className={field}
                />
                {/* Email and phone used to share a two-column row. The phone
                    field is not a plain input — it carries a country selector
                    with a flag, a dial code and a chevron ahead of the number
                    — so at half the modal's width there was nothing left for
                    the number itself, and its placeholder was clipped to
                    "Phone". Each field gets its own row now. */}
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={update}
                  placeholder="Your email"
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  enterKeyHint="next"
                  className={field}
                />
                <PhoneField
                  name="phone"
                  value={form.phone}
                  onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                  placeholder="Phone number"
                  ariaLabel="Phone number"
                />
                <select
                  name="interest"
                  required
                  value={form.interest}
                  onChange={update}
                  aria-label="What can we help you with?"
                  className={field + ' appearance-none'}
                >
                  <option value="">What can we help you with?</option>
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>
                      {i === 'Other' ? 'Not sure yet' : i}
                    </option>
                  ))}
                </select>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={update}
                  placeholder="A line or two about the project"
                  enterKeyHint="send"
                  className={field + ' resize-none'}
                />
              </div>

              {status === 'error' && (
                <p role="alert" className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1d4ed8] px-6 py-4 text-[16px] font-semibold text-white shadow-[0_12px_30px_-12px_rgba(29,78,216,0.65)] transition-all hover:-translate-y-0.5 hover:bg-[#1e40af] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 sm:py-3.5 sm:text-[14px]"
              >
                {status === 'submitting' ? (
                  <>
                    <svg viewBox="0 0 24 24" className="size-4 animate-spin" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M21 12a9 9 0 1 1-6.2-8.5" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  ctx.submit
                )}
              </button>

            </form>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export function ContactModalProvider({ children }) {
  const [raw, setRaw] = useState(null)

  /* open() accepts either a plain subject string, for the old call style, or
     a context object from the click handler below. */
  const open = useCallback((arg = '') => {
    const next = typeof arg === 'string' ? { subject: arg } : arg || {}
    setRaw({ path: window.location.pathname, ...next })
  }, [])
  const close = useCallback(() => setRaw(null), [])

  const ctx = useMemo(() => (raw ? composeContext(raw) : null), [raw])

  /* One capture-phase listener covers every CTA on the site, including pages
     added later, without touching a single page component. */
  useEffect(() => {
    const onClick = (e) => {
      // Let the browser handle new-tab / download / modified clicks.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      // On the contact page itself the form is already on screen; putting a
      // dialog over it would be asking twice for the same thing.
      if (window.location.pathname.replace(/\/+$/, '') === '/contact') return
      const a = e.target.closest?.('a[href]')
      if (!a || !isCtaLink(a)) return
      e.preventDefault()

      const label = (a.innerText || a.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60)
      open({
        path: window.location.pathname,
        label,
        section: sectionLabel(a, label),
        subject: subjectFromHref(a.getAttribute('href') || ''),
        /* Per-button overrides, for the cases where the tables are not the
           right answer and the page knows better. */
        overrideIntent: a.dataset.modalIntent || '',
        title: a.dataset.modalTitle || '',
        desc: a.dataset.modalDesc || '',
        eyebrow: a.dataset.modalEyebrow || '',
        submit: a.dataset.modalSubmit || '',
      })
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [open])

  return (
    <ContactModalContext.Provider value={{ open, close }}>
      {children}
      {ctx && <Dialog onClose={close} ctx={ctx} />}
    </ContactModalContext.Provider>
  )
}

export default ContactModalProvider
